import React from 'react'
import {
  MetaEmpty,
  MetaRoot,
  MetaKv,
  MetaKey,
  MetaVal,
  MetaSep,
} from './PensionLogs.styles'

const SCRAPER_KEYS = [
  'target_month',
  'targetMonth',
  'duration_seconds',
  'processed_months',
  'value',
  'utm_value',
  'utmValue',
  'source',
  'started_at',
  'finished_at',
  'failed_at',
  'last_saved_month',
  'start_month',
  'limit_month',
  'name',
  'message',
]

const SENDER_KEYS = [
  'userName',
  'user',
  'targetMonth',
  'target_month',
  'utmValue',
  'utm_value',
  'pensionValue',
  'pensionFactor',
  'utmSource',
  'subject',
  'retrievedAt',
  'duration_seconds',
  'name',
  'message',
]

const KEY_LABELS = {
  target_month: 'month',
  targetMonth: 'month',
  duration_seconds: 'duration',
  processed_months: 'months',
  value: 'utm',
  utm_value: 'utm',
  utmValue: 'utm',
  source: 'source',
  started_at: 'started',
  finished_at: 'finished',
  failed_at: 'failed',
  last_saved_month: 'last_saved',
  start_month: 'start',
  limit_month: 'limit',
  name: 'error',
  message: 'detail',
  userName: 'user',
  user: 'user',
  pensionValue: 'pension',
  pensionFactor: 'factor',
  utmSource: 'source',
  subject: 'subject',
  retrievedAt: 'retrieved',
}

export const isEmptyMetadata = (value) =>
  value == null ||
  value === '' ||
  (typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0)

const isIsoLike = (value) =>
  typeof value === 'string' &&
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)

export const formatScalar = (value, key) => {
  if (value == null) return null
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') {
    if (key === 'duration_seconds') return `${value}s`
    if (Number.isInteger(value) && Math.abs(value) >= 1000) {
      return value.toLocaleString('es-CL')
    }
    return String(value)
  }
  if (typeof value === 'string') {
    if (!value.trim()) return null
    if (key === 'duration_seconds' && !value.endsWith('s')) return `${value}s`
    if (isIsoLike(value)) {
      try {
        return new Date(value).toLocaleString('es-CL', {
          dateStyle: 'short',
          timeStyle: 'medium',
        })
      } catch {
        return value
      }
    }
    return value
  }
  return null
}

const extractNestedPairs = (key, value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  if (key === 'utmData' || key === 'utm_value' || key === 'currentUtm') {
    const pairs = []
    const utmVal = value.value ?? value.utmValue
    if (utmVal != null) {
      pairs.push({
        key: 'utm',
        label: 'utm',
        value: formatScalar(utmVal, 'utmValue') || String(utmVal),
      })
    }
    if (value.source || value.utmSource) {
      pairs.push({
        key: 'source',
        label: 'source',
        value: String(value.source || value.utmSource),
      })
    }
    const month = value.target_month || value.targetMonth
    if (month) {
      pairs.push({
        key: 'month',
        label: 'month',
        value: formatScalar(month, 'target_month') || String(month),
      })
    }
    return pairs.length ? pairs : null
  }

  return null
}

export const buildMetadataPairs = (metadata, context, limit = 6) => {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return []
  }

  const preferred = context === 'SENDER' ? SENDER_KEYS : SCRAPER_KEYS
  const pairs = []
  const seen = new Set()

  const pushPair = (rawKey, label, rawValue) => {
    if (seen.has(rawKey) || seen.has(label)) return
    const nested = extractNestedPairs(rawKey, rawValue)
    if (nested) {
      nested.forEach((pair) => {
        if (seen.has(pair.key) || seen.has(pair.label)) return
        seen.add(pair.key)
        seen.add(pair.label)
        pairs.push(pair)
      })
      return
    }

    const formatted = formatScalar(rawValue, rawKey)
    if (formatted == null) return
    seen.add(rawKey)
    seen.add(label)
    pairs.push({ key: rawKey, label, value: formatted })
  }

  preferred.forEach((key) => {
    if (!(key in metadata)) return
    pushPair(key, KEY_LABELS[key] || key, metadata[key])
  })

  ;['utmData', 'utm_value', 'currentUtm'].forEach((key) => {
    if (!(key in metadata)) return
    pushPair(key, KEY_LABELS[key] || key, metadata[key])
  })

  if (pairs.length === 0) {
    Object.entries(metadata).forEach(([key, value]) => {
      if (key === 'stack') return
      pushPair(key, KEY_LABELS[key] || key, value)
    })
  }

  return typeof limit === 'number' ? pairs.slice(0, limit) : pairs
}

const LogMetadata = ({ metadata, context }) => {
  if (isEmptyMetadata(metadata)) {
    return <MetaEmpty>—</MetaEmpty>
  }

  if (typeof metadata !== 'object' || Array.isArray(metadata)) {
    const scalar = formatScalar(metadata) ?? String(metadata)
    return (
      <MetaRoot>
        <MetaKv title={scalar}>
          <MetaVal>{scalar}</MetaVal>
        </MetaKv>
      </MetaRoot>
    )
  }

  const pairs = buildMetadataPairs(metadata, context)
  const kvTitle = pairs.map((p) => `${p.label}=${p.value}`).join(' ')

  if (pairs.length === 0) {
    return <MetaEmpty>{'{…}'}</MetaEmpty>
  }

  return (
    <MetaRoot>
      <MetaKv title={kvTitle}>
        {pairs.map((pair, index) => (
          <React.Fragment key={`${pair.key}-${pair.value}`}>
            {index > 0 && <MetaSep>·</MetaSep>}
            <MetaKey>{pair.label}=</MetaKey>
            <MetaVal>{pair.value}</MetaVal>
          </React.Fragment>
        ))}
      </MetaKv>
    </MetaRoot>
  )
}

export default LogMetadata
